// Router + auth gate. Captures the OAuth fragment on first land, then
// bounces to /login if no token, otherwise renders the operator shell.
import { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { getToken, captureTokenFromFragment, logout } from "./auth";
import UsageChip from "./components/UsageChip";
import { Logo } from "./components/Logo";
import { NavSlotProvider, useNavSlotNode } from "./lib/nav_slot";

const Login     = lazy(() => import("./routes/Login"));
const Dashboard = lazy(() => import("./routes/Dashboard"));
const NewApp    = lazy(() => import("./routes/NewApp"));
const AppDetail = lazy(() => import("./routes/AppDetail"));
const SharedApp = lazy(() => import("./routes/SharedApp"));
const Account   = lazy(() => import("./routes/Account"));
const Legal     = lazy(() => import("./routes/Legal"));
const OAuthCallback = lazy(() => import("./routes/OAuthCallback"));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const loc = useLocation();
  if (!getToken()) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return <>{children}</>;
}

/**
 * La pastille de consommation est-elle dans la barre ?
 *
 * NON, décidé le 2026-10-07. Elle comptait des JETONS — notre unité de facturation amont, pas
 * celle que le client achète — et la poser en permanence fait surveiller une dépense au lieu de
 * regarder son app.
 *
 * RIEN N EST PERDU : la page Compte porte le solde et la consommation du mois, et « Account »
 * reste dans la barre, à deux pixels d ici. Un interrupteur et non une suppression : le composant
 * et son sondage restent, éprouvés.
 */
const USAGE_CHIP_SHOWN: boolean = false;

function TopBar() {
  const nav = useNavigate();
  // Route-injected control (e.g. the workspace Preview|Data toggle). Centered
  // between the left nav and the account group; absent on routes that don't set it.
  const centerSlot = useNavSlotNode("center");
  const rightSlot  = useNavSlotNode("right");
  return (
    // `sticky top-0` keeps the bar in view as the operator scrolls long
    // pages (theme + font pickers). Translucent + blur so whatever the
    // page paints scrolls underneath it cleanly.
    <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur supports-[backdrop-filter]:bg-bg/70">
      {/* No max-width cap — navbar spans the full viewport on big screens
          (logo flush-left, account chip flush-right). Page content can
          still be constrained per route by the Shell width prop; the
          navbar is independent. */}
      {/* `px-4 sm:px-10` — 80 px de marge sur un écran de 390 px, c'est un cinquième de
          l'écran donné au vide avant même d'afficher quoi que ce soit. */}
      {/* UNE SEULE CHOSE CÈDE : le créneau du milieu, et dedans la pastille défile. Le
          logo, les liens et le groupe de droite portent `shrink-0` — sans quoi flex les
          comprime TOUS quand la rangée déborde, et leur texte sort de leur boîte
          rétrécie. Capture de l'opérateur du 2026-10-02 : « perimetr.platfor… » coupé avec
          PREVIEW par-dessus, « Account » sous les onglets, « 142.0M token » tranché par
          Publish. */}
      <div className="flex h-14 items-center gap-4 px-4 sm:px-10">
        <Link to="/" className="flex shrink-0 items-center gap-2 text-base font-semibold tracking-tight">
          {/* LA MARQUE ET LE NOM. Le nom est « getperimetr » depuis le 2026-10-07 : c'est celui
              du produit, et « perimetr.platform » nommait notre tuyauterie, pas ce que
              l'opérateur achète.
              MESURÉ le 2026-10-04 : la barre faisait 521 px de contenu minimum, donc elle
              débordait de 161 px sur un écran de 360. Tout y porte `shrink-0` — ce qui est juste
              (sans quoi flex coupe les textes) mais transforme la compression en débordement. La
              seule issue honnête est d'en PORTER MOINS sur un petit écran, et c'est précisément
              ce à quoi sert une marque : sous `sm` il ne reste QUE le dessin — 20 px, contre les
              ~75 px du mot qu'il remplace. La barre y gagne. */}
          <Logo className="h-7 w-7 shrink-0 text-accent" />
          <span className="hidden sm:inline"><span className="text-accent">get</span>perimetr</span>
        </Link>
        <nav className="ml-2 flex shrink-0 items-center gap-1">
          <Link to="/" className="rounded-md px-3 py-1.5 text-[13px] font-medium text-text-secondary hover:text-text">Apps</Link>
          <Link to="/account" className="rounded-md px-3 py-1.5 text-[13px] font-medium text-text-secondary hover:text-text">Account</Link>
        </nav>
        {/* Centered route slot — flex-1 so it sits in the middle and pushes the
            account group to the right. Only takes space when a route injects. */}
        {/* `min-w-0` : le créneau porte la pastille d'onglets, qui défile. Sans lui, cet
            enfant flex garde la largeur de son contenu (~530 px) et pousse la barre hors
            de l'écran au lieu de laisser la pastille défiler. */}
        {centerSlot && <div className="flex min-w-0 flex-1 justify-center">{centerSlot}</div>}
        <div className="ml-auto flex shrink-0 items-center gap-3 text-[13px]">
          {/* Token usage counter — operator-scoped, polls /usage every 15s.
              RANGÉE le 2026-10-07 : « hide the token consumption chip ». Un compteur de jetons
              posé en permanence dans la barre fait surveiller une dépense au lieu de regarder son
              app — et il dit une unité qui n'est pas celle du client.
              RIEN N'EST PERDU, et c'était déjà vrai quand elle disparaissait sous `sm` : la page
              Compte montre le solde ET ce que le mois a consommé, et le lien « Account » reste
              dans la barre. L'interrupteur garde la pastille à une ligne de son retour. */}
          {USAGE_CHIP_SHOWN && <span className="hidden sm:inline-flex"><UsageChip /></span>}
          {/* The active route's own control, beside the account group: today the app's settings
              gear. It sits HERE and not in the centre group because the centre group only appears
              once an app has built — and settings is where you go when the app is NOT fine. */}
          {rightSlot}
          {/* NO E-MAIL HERE.
              It told the operator their own address, on every page, for ever — the one fact they are
              least likely to have forgotten. It was also the widest thing in the bar, pushing the
              controls that DO something toward the middle.
              Where it belongs, and still is: the Account page, beside the plan and the erase-my-data
              confirmation, where knowing which identity you are acting on actually matters. */}
          {/* ELLE RESTE, et c'est vérifié : la page Compte n'offre AUCUNE déconnexion (son
              seul `logout` suit l'effacement du compte). La cacher sur un téléphone y
              enfermerait l'utilisateur. Seul le mot part ; l'icône reste, avec son nom
              accessible — une cible de 32 px, au-dessus du minimum de WCAG 2.5.8. */}
          <button onClick={() => { logout(); nav("/login", { replace: true }); }}
                  title="Sign out" aria-label="Sign out"
                  className="flex h-8 items-center gap-1.5 rounded-md px-2 font-medium text-text-secondary hover:text-text sm:px-3">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </div>
    </header>
  );
}

// Shell width mode:
//   - "constrained" (default): centered, max-w-[1600px], generous page
//     padding. The right idiom for forms + lists (Dashboard, NewApp,
//     Login) — text is easier to read at a bounded width, controls stay
//     near each other.
//   - "full": no max-width, no page padding. The right idiom for the gen
//     workspace, which wants to fill the viewport on big monitors so the
//     iframe has room to breathe.
function Shell({ children, width = "constrained" }: { children: React.ReactNode; width?: "constrained" | "full" }) {
  const wrapper = width === "full"
    ? ""                                    // edge-to-edge
    // `px-4 sm:px-10` — 80 px de marge de chaque côté sur un écran de 390 px, c'est 40 %
    // de l'écran donné au vide avant d'afficher quoi que ce soit. Même correction que la
    // barre juste au-dessus, et pour la même raison. `py-8 sm:py-12` suit : sur un
    // téléphone, 48 px en haut repoussent le contenu sous le pli pour rien.
    : "mx-auto max-w-[1600px] px-4 py-8 sm:px-10 sm:py-12"; // capped + padded
  return (
    <div className="min-h-screen bg-bg text-text">
      <TopBar />
      <main className={wrapper}>
        <Suspense fallback={<div className="py-16 text-center text-sm text-text-secondary">Loading…</div>}>
          {children}
        </Suspense>
      </main>
    </div>
  );
}

export default function App() {
  // Capture OAuth fragment on first paint. setReady gates render so the
  // RequireAuth check sees the token if the bounce just landed.
  const [ready, setReady] = useState(false);
  useEffect(() => { captureTokenFromFragment(); setReady(true); }, []);
  if (!ready) return null;
  return (
    <BrowserRouter>
      <NavSlotProvider>
        <Routes>
          <Route path="/login" element={<Suspense fallback={null}><Login /></Suspense>} />
          <Route path="/"            element={<RequireAuth><Shell><Dashboard /></Shell></RequireAuth>} />
          <Route path="/new"         element={<RequireAuth><Shell><NewApp /></Shell></RequireAuth>} />
          <Route path="/account"     element={<RequireAuth><Shell><Account /></Shell></RequireAuth>} />
          {/* Public: readable before signing in (the top bar polls /usage, which needs a session). */}
          <Route path="/legal/:doc?" element={<Suspense fallback={null}><div className="min-h-screen bg-bg text-text"><main className="mx-auto max-w-[900px] px-6 py-12"><Legal /></main></div></Suspense>} />
          <Route path="/apps/:slug"  element={<RequireAuth><Shell width="full"><AppDetail /></Shell></RequireAuth>} />
          <Route path="/shared/:slug" element={<RequireAuth><Shell width="full"><SharedApp /></Shell></RequireAuth>} />
          {/* Where a service's « se connecter avec… » window comes back — the address the owner registers there. */}
          <Route path="/oauth/callback/:slug/:name" element={<RequireAuth><Shell><OAuthCallback /></Shell></RequireAuth>} />
          {/* PERIMETR's app at a service (platform-oauth): ONE address per provider, registered once. */}
          <Route path="/oauth/callback/:provider" element={<RequireAuth><Shell><OAuthCallback /></Shell></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </NavSlotProvider>
    </BrowserRouter>
  );
}
